// The institution's crest as the console's anchor (PRD-60 chunk 0).
//
// For a director, seeing their own crest in the header is recognition; for a
// Platonica superuser it is the clearest possible answer to "which
// institution am I looking at" — clearer than a select. The logos already
// exist (PRD-55), and the mark follows the same three rules as the SPA's
// SchoolMark:
//
// 1. A FIXED LIGHT CHIP in both themes: crests are drawn for paper, and a
//    dark-navy shield on the dark canvas is a black rectangle.
// 2. NOT A SQUARE: crests are as often 4:1 wordmarks as shields. Fixed
//    height, flexible width, `object-contain`.
// 3. The box is reserved before the image loads, and a monogram sits there
//    until then — and stays there for the institutions that never upload one.

import { cn } from "@lib/cn"
import { useState } from "react"

const sizes = {
  /** The app header. */
  md: { box: "h-9", logo: "h-7 max-w-[5.5rem]", mono: "w-9 text-sm", px: 36 },
  /** The public pages, where the institution leads (PRD-60 chunk A). */
  lg: { box: "h-14", logo: "h-11 max-w-[9rem]", mono: "w-14 text-xl", px: 56 },
} as const

export function InstitutionMark({
  name,
  logoUrl,
  size = "md",
  className,
}: {
  name: string
  logoUrl?: string | null
  size?: keyof typeof sizes
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const showLogo = !!logoUrl && !failed
  const monogram = name.trim().charAt(0).toUpperCase() || "P"
  const s = sizes[size]

  return (
    <span
      aria-hidden="true"
      className={cn(
        "rounded-control inline-flex shrink-0 items-center justify-center overflow-hidden",
        s.box,
        showLogo
          ? "border-border w-auto border bg-white px-1.5"
          : cn("bg-accent-soft text-accent-ink font-semibold", s.mono),
        className,
      )}
    >
      {showLogo ? (
        <img
          src={logoUrl}
          alt=""
          height={s.px}
          className={cn("w-auto object-contain", s.logo)}
          onError={() => setFailed(true)}
        />
      ) : (
        monogram
      )}
    </span>
  )
}
